import React from 'react';
import { PolicyPanel } from './policy-panel.jsx';
import { api } from '../api.js';
import { Empty, Icon, Lbl, Loading, Modal, PH, Panel } from './components.jsx';
import { PeoplePicker } from './people-picker.jsx';
import { PEOPLE } from './data.jsx';
import { Button, Checkbox, DataTable, Field, Pill } from './ds/fgcp-ds.jsx';

// approval-forms.jsx — 결재 양식 관리(approval_admin·system_admin). 권한은 서버가 판단한다(목록 API의 actions.manage, 관리 API 403).
const { useState: uS, useEffect: uE, useCallback: uC } = React;

const TYPE_LABEL = { text: '한 줄 글', textarea: '여러 줄 글', number: '숫자', money: '금액(원)', date: '날짜', period: '기간', select: '선택' };
const box = { padding: '7px 9px', border: '1px solid var(--line-strong)', borderRadius: 'var(--r-ctl)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 12.5, boxSizing: 'border-box' };

/** 기본 결재선 줄 종류 — 서버 forms.ts FormLineKind와 같은 값 */
const FORM_LINE_KIND = { approve: '결재', agree: '합의', cc: '참조', view: '열람' };
/** 시스템 양식을 쓰는 화면 — 서버 approval_forms.system_kind 값 */
const SYSTEM_KIND = { leave_request: '휴가' };

/**
 * 양식 편집 창 — 항목 정의와 기본 결재선(사람 또는 기안자 기준 n번째 위 부서장). 고친 정의는 새 문서부터 쓰인다.
 * 시스템 양식(휴가신청서 등)은 담당 모듈이 항목을 채운다 — 이름·항목·사용 여부는 서버가 거부하므로 화면에서도 고치지 못하게 보여 준다(설명·기본 결재선만).
 */
function FormEditor({ form, onClose, onSaved, onToast }) {
  const system = form?.systemKind ? SYSTEM_KIND[form.systemKind] ?? form.systemKind : null;
  const [name, setName] = uS(form?.name ?? '');
  const [description, setDescription] = uS(form?.description ?? '');
  const [active, setActive] = uS(form?.active ?? true);
  const [fields, setFields] = uS(() => (form?.fields ?? [{ key: 'body', label: '내용', type: 'textarea', required: false }])
    .map(f => ({ ...f, optionsText: (f.options ?? []).join(', ') })));
  const [lines, setLines] = uS(() => (form?.lines ?? [{ kind: 'approve', managerLevel: 1 }])
    .map(l => ({ kind: l.kind, target: l.userId ? 'user' : 'manager', managerLevel: l.managerLevel ?? 1, userId: l.userId ?? null })));
  const [saving, setSaving] = uS(false);

  const setField = (i, patch) => setFields(fs => fs.map((f, j) => (j === i ? { ...f, ...patch } : patch.summary ? { ...f, summary: false } : f)));
  const move = (list, i, d) => { const a = [...list]; const [x] = a.splice(i, 1); a.splice(i + d, 0, x); return a; };
  const save = () => {
    setSaving(true);
    const body = {
      name, description, active,
      schema: { fields: fields.map(f => ({
        key: f.key.trim(), label: f.label, type: f.type, required: Boolean(f.required), summary: Boolean(f.summary),
        ...(f.type === 'select' ? { options: f.optionsText.split(',').map(o => o.trim()).filter(Boolean) } : {})
      })) },
      lines: lines.map(l => (l.target === 'user' ? { kind: l.kind, userId: l.userId } : { kind: l.kind, managerLevel: Number(l.managerLevel) }))
    };
    (form ? api.put('/api/v1/approvals/forms/' + form.id, body) : api.post('/api/v1/approvals/forms', body))
      .then(() => { onToast(form ? '양식을 고쳤습니다 — 새 문서부터 쓰입니다' : '양식을 만들었습니다'); onSaved(); })
      .catch(e => onToast(e.message))
      .finally(() => setSaving(false));
  };
  return (
    <Modal width={760} title={form ? '양식 고치기' : '새 양식'} icon="file-text" onClose={onClose}
      footer={<React.Fragment>
        <Button variant="secondary" size="sm" onClick={onClose}>취소</Button>
        <Button variant="primary" size="sm" disabled={saving || !name.trim()} onClick={save}>저장</Button>
      </React.Fragment>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxHeight: '64vh', overflowY: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'end' }}>
          <Field label="양식 이름" value={name} onChange={e => setName(e.target.value)} disabled={Boolean(system)} />
          {system
            ? <span style={{ fontSize: 12, color: 'var(--ink-3)', paddingBottom: 10 }} data-system-form>{system} 화면에서 쓰는 시스템 양식</span>
            : <Checkbox label="사용(기안 목록에 보임)" checked={active} onChange={setActive} style={{ paddingBottom: 10 }} />}
        </div>
        <Field label="설명" value={description} onChange={e => setDescription(e.target.value)} placeholder="기안할 때 양식 아래에 보이는 안내" />
        {system ? (
          <div>
            <Lbl style={{ marginBottom: 8 }}>입력 항목 <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>— {system} 화면이 채운다(고칠 수 없다)</span></Lbl>
            <div style={{ fontSize: 12.5, color: 'var(--ink-2)' }} data-form-fields-fixed>{fields.map(f => `${f.label}(${TYPE_LABEL[f.type] ?? f.type})`).join(' · ')}</div>
          </div>
        ) : (
        <div>
          <Lbl style={{ marginBottom: 8 }}>입력 항목 <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>— 키는 영문(저장용), '요약'은 목록의 금액/기간 칸</span></Lbl>
          {fields.map((f, i) => (
            <div key={i} data-form-field={i} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 110px auto auto auto', gap: 6, alignItems: 'center', marginBottom: 6 }}>
              <input style={{ ...box, fontFamily: 'var(--font-mono)' }} placeholder="key" value={f.key} onChange={e => setField(i, { key: e.target.value })} aria-label="항목 키" />
              <input style={box} placeholder="이름" value={f.label} onChange={e => setField(i, { label: e.target.value })} aria-label="항목 이름" />
              <select style={box} value={f.type} onChange={e => setField(i, { type: e.target.value })} aria-label="항목 종류">
                {Object.entries(TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <Checkbox size="sm" label="필수" checked={Boolean(f.required)} onChange={v => setField(i, { required: v })} />
              <Checkbox size="sm" label="요약" description={i === 0 ? '목록에 보일 항목 하나' : undefined} checked={Boolean(f.summary)} onChange={v => setField(i, { summary: v })} />
              <span style={{ display: 'inline-flex', gap: 2 }}>
                <button className="ico" disabled={i === 0} onClick={() => setFields(fs => move(fs, i, -1))} aria-label="위로"><Icon name="chevron-down" size={12} style={{ transform: 'rotate(180deg)' }} /></button>
                <button className="ico" disabled={i === fields.length - 1} onClick={() => setFields(fs => move(fs, i, 1))} aria-label="아래로"><Icon name="chevron-down" size={12} /></button>
                <button className="ico" onClick={() => setFields(fs => fs.filter((_, j) => j !== i))} aria-label="항목 빼기"><Icon name="x" size={12} /></button>
              </span>
              {f.type === 'select' && <input style={{ ...box, gridColumn: '2 / 4' }} placeholder="보기(쉼표로 나눔)" value={f.optionsText} onChange={e => setField(i, { optionsText: e.target.value })} aria-label="보기" />}
            </div>
          ))}
          <Button size="sm" variant="ghost" style={{ alignSelf: 'flex-start' }} onClick={() => setFields(fs => [...fs, { key: '', label: '', type: 'text', required: false, optionsText: '' }])}>+ 항목</Button>
        </div>
        )}
        <div>
          <Lbl style={{ marginBottom: 8 }}>기본 결재선 <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>
            {system ? `— 신청자 기준으로 풀린다(신청자는 첫 결재자만 바꿀 수 있다, 사람을 못 찾은 줄은 빠진다)` : '— 기안할 때 채워지고 기안자가 고칠 수 있다'}
          </span></Lbl>
          {lines.map((l, i) => (
            <div key={i} data-form-line={i} style={{ display: 'grid', gridTemplateColumns: '90px 150px 1fr auto', gap: 6, alignItems: 'start', marginBottom: 6 }}>
              {/* 결재·합의는 이 순서대로 처리하는 결재선, 참조·열람은 순서 없는 수신자(참조는 상신 때, 열람은 최종 승인 때 알림) */}
              <select style={box} value={l.kind} aria-label={`${i + 1}번째 줄 종류`}
                onChange={e => setLines(ls => ls.map((x, j) => (j === i ? { ...x, kind: e.target.value } : x)))}>
                {Object.entries(FORM_LINE_KIND).map(([k, label]) => <option key={k} value={k}>{i + 1}. {label}</option>)}
              </select>
              <select style={box} value={l.target === 'user' ? 'user' : 'm' + l.managerLevel}
                onChange={e => setLines(ls => ls.map((x, j) => (j !== i ? x : e.target.value === 'user' ? { ...x, target: 'user' } : { ...x, target: 'manager', managerLevel: Number(e.target.value.slice(1)) })))}
                aria-label="결재자 정하는 방법">
                {[1, 2, 3, 4, 5].map(n => <option key={n} value={'m' + n}>{n === 1 ? '기안자 부서장' : `${n}번째 위 부서장`}</option>)}
                <option value="user">사람 지정</option>
              </select>
              {l.target === 'user'
                ? <PeoplePicker label="" value={l.userId ? [l.userId] : []} max={1} onChange={v => setLines(ls => ls.map((x, j) => (j === i ? { ...x, userId: v[0] ?? null } : x)))} />
                : <span style={{ fontSize: 11.5, color: 'var(--ink-3)', paddingTop: 8 }}>기안자에 따라 정해집니다(재직 중·기안자 본인 제외)</span>}
              <button className="ico" onClick={() => setLines(ls => ls.filter((_, j) => j !== i))} aria-label="결재선 줄 빼기"><Icon name="x" size={12} /></button>
            </div>
          ))}
          <Button size="sm" variant="ghost" style={{ alignSelf: 'flex-start' }} onClick={() => setLines(ls => [...ls, { kind: 'approve', target: 'manager', managerLevel: Math.min(5, ls.length + 1), userId: null }])}>+ 결재선 줄</Button>
        </div>
      </div>
    </Modal>
  );
}

/** 양식 관리 탭 */
export function FormManager({ onToast }) {
  const [forms, setForms] = uS(null);
  const [editing, setEditing] = uS(null);   // 고칠 양식 또는 'new'
  const load = uC(() => api.get('/api/v1/approvals/forms/manage').then(r => setForms(r.forms)).catch(e => onToast(e.message)), [onToast]);
  uE(() => { void load(); }, [load]);
  const personName = id => PEOPLE.find(p => p.id === id)?.name ?? '퇴사·없는 사람';
  const who = l => (l.userId ? personName(l.userId) : l.managerLevel === 1 ? '부서장' : `${l.managerLevel}번째 위 부서장`);
  const lineText = f => f.lines.map(l => `${FORM_LINE_KIND[l.kind] ?? l.kind} ${who(l)}`).join(' → ') || '없음';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <Panel scroll>
      <PH title="결재 양식" sub={forms ? forms.length + '개' : ''} right={<Button variant="primary" size="sm" onClick={() => setEditing('new')}>새 양식</Button>} />
      {!forms ? <Loading title="불러오는 중" /> : forms.length === 0 ? <Empty title="양식이 없습니다" sub="새 양식을 만드세요" /> : (
        <DataTable columns={[
          { key: 'name', header: '양식', strong: true, render: f => <button className="link" onClick={() => setEditing(f)}>{f.name}</button> },
          { key: 'fields', header: '항목', render: f => <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>{f.fields.map(x => x.label).join(' · ')}</span> },
          { key: 'line', header: '기본 결재선', render: f => <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>{lineText(f)}</span> },
          { key: 'documentCount', header: '문서', mono: true, align: 'right', width: 56 },
          { key: 'active', header: '상태', width: 70, render: f => f.systemKind ? <Pill tone="info" dot={false}>시스템</Pill> : f.active ? <Pill tone="ok" dot>사용</Pill> : <Pill tone="neutral" dot={false}>중지</Pill> },
        ]} rows={forms} />
      )}
      {editing && <FormEditor form={editing === 'new' ? null : editing} onToast={onToast} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void load(); }} />}
    </Panel>
    <PolicyPanel prefix="approval." title="결재 정책" onToast={onToast} />
    </div>
  );
}
